import React, { FC } from "react";
import { useSelector } from "react-redux";
import TableTab from "../../../components/tabs/table-tab";
import { toast } from "react-toastify";
import { FILTER_DATE, FILTER_ACTION_TYPE, FILTER_TYPE, FILTER_SUB_TYPE } from "../../../constants/filter-types";
import { REPORTS_AUDIT } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { DEF_PG_SIZE_REPORTS } from '../../../constants/maximum-page-size';
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import AuditReportColumn from "../../../models/ColumnDefinitions/AuditReportColumn";
import { makeFilterOnlyColumns, removeColumns } from "../../../models/utilities/columnHelper";
import { Filter } from "../../../models/Filter";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import AuditOperatorSearch from "../../../models/search/AuditOperatorSearch";
import AuditTypeSearch from "../../../models/search/AuditTypeSearch";
import { getLoginNamesWithPagination } from "../../../services/loginService";
import { auditRunReport, getAuditTypeNamesWithPagination, getAuditSubTypeNamesWithPagination, getAuditActionTypeNamesWithPagination } from "../../../services/auditService";
import { getUserFiltersByView } from "../../../services/userPreferenceService";
import { getAuditReport } from "../../../services/reportService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import AuditSubTypeSearch from "@/models/search/AuditSubTypeSearch";
import AuditActionTypeSearch from "@/models/search/AuditActionTypeSearch";
import AuditReportSearch from "@/models/search/AuditReportSearch";
import { showError } from "../../../services/utilities/ServiceError";
import { getTodayDate } from "../../../utilities/DateUtils";
import { IAppState } from "@/store/types";

interface AuditReportProps {
    reportId: string;
}

const AuditReport: FC<AuditReportProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const lookup: Lookup[] = [
        {
            name: LookupTypes.auditType,
            values: [],
            dataMethod: getAuditTypeNamesWithPagination,
            searchAccessor: AuditTypeSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.auditSubType,
            values: [],
            dataMethod: getAuditSubTypeNamesWithPagination,
            searchAccessor: AuditSubTypeSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.auditActionType,
            values: [],
            dataMethod: getAuditActionTypeNamesWithPagination,
            searchAccessor: AuditActionTypeSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: "userLoginId",
            values: [],
            dataMethod: (listParameters) => getLoginNamesWithPagination(listParameters, parseInt(user.profileId), true),
            searchAccessor: AuditOperatorSearch.find(column => column.accessor === "name")?.searchOnName
        }
    ];

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = REPORTS_AUDIT;

    const getOriginalStaticFilter = async (filterKey: string): Promise<Filter> => {
        switch (filterKey) {
            case "dateOnly":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    clearPolicy: "locked",
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.date),
                    options: [],
                    selected: [{
                        label: getTodayDate(),
                        value: getTodayDate(),
                    }],
                };
            case "auditActionType.id":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.action),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            case "auditType.id":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.auditType),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            case "auditSubType.id":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.auditSubType),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            default:
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: filterKey,
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
        }
    };

    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys) => void) => {
        // Filters that should always show for this view
        const staticFilterKeys = ["dateOnly", "auditActionType.id", "auditType.id", "auditSubType.id"];

        //Add custom filter
        let customFilter: Filter[] = [];
        getUserFiltersByView(userLoginId, currentMenuDetail, reportId).then(async (response) => {
            const dateFilter = response.find(filter => filter.filterId === FILTER_DATE);
            if (!!!dateFilter) {
                customFilter.push({
                    isCustomFilter: true,
                    isStatic: true,
                    clearPolicy: "locked",
                    key: "dateOnly",
                    label: translate.byKey(TranslationKeys.date),
                    options: [],
                    selected: [{
                        label: getTodayDate(),
                        value: getTodayDate(),
                    }],
                });
            }
            if (!response.find(filter => filter.filterId === FILTER_ACTION_TYPE)) {
                customFilter.push(await getOriginalStaticFilter("auditActionType.id"));
            }
            if (!response.find(filter => filter.filterId === FILTER_TYPE)) {
                customFilter.push(await getOriginalStaticFilter("auditType.id"));
            }
            if (!response.find(filter => filter.filterId === FILTER_SUB_TYPE)) {
                customFilter.push(await getOriginalStaticFilter("auditSubType.id"));
            }
        }).catch((error) => {
            showError(error, toast.warn);
        }).finally(() => {
            setDetails(userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys);
        })
        
        // setDetails(userLoginId, currentMenuDetail, [], reportId);
    }
    // <-- end context

    const getDataMethod = async (listParameters?: any) => {
        try {
            await auditRunReport(reportId);
            return getAuditReport(translate, { ...listParameters, filters: listParameters?.filters });
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    }

    return (<TableContextProvider
        maxPageSize={DEF_PG_SIZE_REPORTS}
        dataMethod={(listParameters) => getDataMethod(listParameters)}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={makeFilterOnlyColumns(removeColumns(AuditReportColumn, ['oldValue', 'newValue']), ['type', 'subType'])}
        searchDescriptor={AuditReportSearch}
        childColumns={removeColumns(AuditReportColumn, ['name', 'ipAddress', 'dateTimeUtc', 'action', 'dateOnly'])}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}
        loadDataOnDemand
        runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}
        getOriginalStaticFilter={getOriginalStaticFilter}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.audit)}
            selectRows={false}
            useGroupedRows
            highlightExpandedParent
            showFilterMenu={true}
            cyName="auditReport" />
    </TableContextProvider >
    );
};

export default AuditReport;

